import { createRef, useEffect, useState } from 'react';
import Page from '../..';
import Table, { TableHeader } from '../../../Table';
import useStore from '../../../Hooks/useStore';
import SVG from '../../../SVG';
import * as S from './Styles';
import {
  formatBRL,
  formatBRLForList,
  formatCentsForList,
} from '../../../../store/masks';
import {
  getFilterColumn,
  getSortColumn,
  setFilterColumn,
  setSortColumn,
} from '../../../../store/utils';
import { useSnackbar } from 'notistack';
import Form from '../../../Form';

export function PageBannerPromotions() {
  const [getters, setters] = useStore();
  const {
    form: { getForm },
    product: { getProduct },
  } = getters;
  const {
    product: {
      setProduct,
      findFilteredPromotionalProducts,
      createSingleFormsForProducts,
      autocompletePromotionalProductItems,
    },
    form: { setForm },
  } = setters;
  const ref = createRef<HTMLDivElement>();
  const { enqueueSnackbar } = useSnackbar();
  const searchFilter = getProduct('filters.promotion.filter');
  const setSearchFilter = (filter: SearchFilter) =>
    setProduct({
      field: 'filters.promotion.filter',
      value: filter,
    });

  const singleForms = getForm('batchBanner.singleForms');

  const products = getProduct('filters.promotion.data.data').filter(
    ({ id, promotionalProductId }) =>
      !singleForms.find(
        ({ product: { product } }) =>
          product.id === id &&
          product.type === 'promotion' &&
          product.promotionalProductId === promotionalProductId,
      ),
  );

  const setPage = (page: number) =>
    setSearchFilter({
      ...searchFilter,
      page,
    });

  const setItemsPerPage = (limit: number) =>
    setSearchFilter({
      ...searchFilter,
      page: 1,
      limit,
    });

  const getFilterField = (field: string, operator?: OPERATOR) =>
    getFilterColumn(field, searchFilter, operator);

  const setFilterField = (field: string, operator?: OPERATOR) => (e) => {
    const value = e.target.value;
    let filter = setFilterColumn(field, value, searchFilter, operator);
    setSearchFilter({
      ...filter,
      page: 1,
    });
  };

  const getSortField = (field: string) => getSortColumn(field, searchFilter);

  const setSortField = (field: string) => (value) =>
    setSearchFilter(setSortColumn(field, value, searchFilter));

  const getAutocompleteItems = (
    field: keyof ProductState['autocompletes']['promotion'],
  ) =>
    getProduct(`autocompletes.promotion.${field}.data`).map((name, id) => ({
      id,
      name,
    }));

  const setAutocompleteItems = (
    field: keyof ProductState['autocompletes']['promotion'],
  ) => {
    autocompletePromotionalProductItems(field)
      .then((data) => {
        setProduct({
          field: `autocompletes.promotion.${field}.data`,
          value: data,
        });
      })
      .catch(() => {
        enqueueSnackbar('Houve uma falha ao buscar os produtos', {
          variant: 'error',
        });
      });
  };

  const setAutocompleteFilterField =
    (field: keyof ProductState['autocompletes']['promotion']) => (e) => {
      setProduct({
        field: `autocompletes.promotion.${field}.filter.value`,
        value: e.target.value,
      });
    };

  const setField = (field: any, operator?: OPERATOR) => (e) => {
    setFilterField(field, operator)(e);
    setAutocompleteFilterField(field)(e);
  };

  const [loadingProducts, setLoadingProducts] = useState(false);

  const [checkedIndexes, setCheckedIndexes] = useState<number[]>([]);

  const checkAll = !products.find(
    (_, index) => !checkedIndexes.includes(index),
  );

  const setCheckAll = (e) => {
    const isChecked = e.target.checked;
    setCheckedIndexes(isChecked ? products.map((_, i) => i) : []);
  };

  const getCheckedIndex = (index: number) => checkedIndexes.includes(index);
  const setCheckedIndex = (index: number) => (e) => {
    const isChecked = e.target.checked;
    const filteredCheckedIndexes = checkedIndexes.filter((i) => i !== index);
    setCheckedIndexes(
      isChecked ? [...filteredCheckedIndexes, index] : filteredCheckedIndexes,
    );
  };

  const onLineClickHandler = (index: number) => (e) => {
    var senderElementName = e.target.tagName.toLowerCase();
    if (['input', 'image', 'svg', 'path', 'button'].includes(senderElementName))
      return;
    const isChecked = checkedIndexes.findIndex((i) => i === index) !== -1;
    const filteredCheckedIndexes = checkedIndexes.filter((i) => i !== index);
    setCheckedIndexes(
      isChecked ? filteredCheckedIndexes : [...filteredCheckedIndexes, index],
    );
  };

  const items = products.map((product, index) => ({
    $isSelected: getCheckedIndex(index),
    code: product.code,
    ean: product.ean,
    resume: product.resume,
    type: product.promotion?.type ?? '-',
    promotion: product.promotion?.description ?? 'GERAL',
    regularPrice: product.price.regularPrice,
    promotionalPrice: product.promotion
      ? product.promotion.promotionalPrice
      : (product.price?.promotionalPrice ?? product.price?.regularPrice),
    promotionalStartDate: product.promotion
      ? product.promotion.promotionalStartDate
      : (product.price?.promotionalStartDate ?? ''),
    promotionalEndDate: product.promotion
      ? product.promotion.promotionalEndDate
      : (product.price?.promotionalEndDate ?? ''),
    check: () => (
      <Form.Input.Checkbox
        checked={getCheckedIndex(index)}
        onChange={setCheckedIndex(index)}
      />
    ),
  }));

  const total = getProduct('filters.promotion.data.meta.total');

  const handleFilter = () => {
    setLoadingProducts(true);
    findFilteredPromotionalProducts(getProduct('filters.promotion.filter'))
      .then((data) => {
        setProduct({
          field: 'filters.promotion.data',
          value: data,
        });
      })
      .catch(() => {
        enqueueSnackbar('Houve uma falha ao buscar os produtos', {
          variant: 'error',
        });
      })
      .finally(() => {
        setLoadingProducts(false);
        setCheckedIndexes([]);
      });
  };

  let searchTimerId: NodeJS.Timeout;

  useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(searchTimerId);
      searchTimerId = setTimeout(() => {
        handleFilter();
      }, 700);
    };
    handleInputChange();
    return () => {
      clearTimeout(searchTimerId);
    };
  }, [searchFilter]);

  const addProducts = () => {
    const selectedProducts: FilteredProductItem[] = products
      .filter((_, index) => checkedIndexes.includes(index))
      .map((product) => ({ ...product, type: 'promotion', identifier: 0 }));
    createSingleFormsForProducts(selectedProducts);
    setCheckedIndexes([]);

    const searchFilter = getProduct('filters.promotion.filter');
    const oldSelectedItemIds = (getForm(
      'batchBanner.filters.products.filter',
    )?.find(({ column }) => column === 'promotionalProductId')?.value ??
      []) as number[];

    const newSelectedItemIds = [
      ...selectedProducts.map(
        ({ promotionalProductId }) => promotionalProductId,
      ),
      ...oldSelectedItemIds,
    ];

    setSearchFilter(
      setFilterColumn(
        'promotionalProductId',
        newSelectedItemIds.length > 0 ? newSelectedItemIds : '',
        searchFilter,
        'NOTIN',
        'AND',
      ),
    );

    if (getForm('batchBanner.batch.productType') === 'promotion') {
      const bannerSearchFilter = getForm('batchBanner.filters.products');
      const setBannerSearchFilter = (filter: SearchFilter) =>
        setForm('batchBanner.filters.products', filter as any);

      setBannerSearchFilter(
        setFilterColumn(
          'promotionalProductId',
          newSelectedItemIds.length > 0 ? newSelectedItemIds : '',
          bannerSearchFilter,
          'NOTIN',
          'AND',
        ),
      );
    }

    const numberOfProducts = selectedProducts.length;
    enqueueSnackbar(
      numberOfProducts === 1
        ? '1 produto adicionado'
        : `${numberOfProducts} produtos adicionados`,
      {
        variant: 'success',
      },
    );
  };
  const headers: TableHeader[] = [
    {
      text: 'Código',
      value: 'code',
      width: '0.45fr',
      filter: {
        value: getFilterField('code'),
        onChange: setField('code'),
        items: getAutocompleteItems('code'),
        handleItems: () => setAutocompleteItems('code'),
        label: 'Código do produto',
      },
      sort: {
        value: getSortField('code'),
        onChange: setSortField('code'),
      },
      tooltip: {
        translateX: 0,
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('code', '', searchFilter));
        setProduct({
          field: `autocompletes.promotion.code.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Código barras',
      value: 'ean',
      width: '0.6fr',

      filter: {
        value: getFilterField('ean'),
        onChange: setField('ean'),
        items: getAutocompleteItems('ean'),
        handleItems: () => setAutocompleteItems('ean'),
        label: 'Código de barras',
      },
      sort: {
        value: getSortField('ean'),
        onChange: setSortField('ean'),
      },
      tooltip: {
        translateX: 0.25,
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('ean', '', searchFilter));
        setProduct({
          field: `autocompletes.promotion.ean.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Descrição',
      value: 'resume',
      width: '1fr',
      filter: {
        value: getFilterField('resume'),
        onChange: setField('resume'),
        items: getAutocompleteItems('resume'),
        handleItems: () => setAutocompleteItems('resume'),
        label: 'Descrição',
      },
      sort: {
        value: getSortField('resume'),
        onChange: setSortField('resume'),
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('resume', '', searchFilter));
        setProduct({
          field: `autocompletes.promotion.resume.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Tipo',
      value: 'type',
      width: '0.75fr',
      filter: {
        value: getFilterField('type'),
        onChange: setField('type'),
        items: getAutocompleteItems('type'),
        handleItems: () => setAutocompleteItems('type'),
        label: 'Tipo da promoção',
      },
      sort: {
        value: getSortField('type'),
        onChange: setSortField('type'),
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('type', '', searchFilter));
        setProduct({
          field: `autocompletes.promotion.type.filter.value`,
          value: '',
        });
      },
    },

    {
      text: 'Promoção',
      value: 'promotion',
      width: '0.75fr',
      filter: {
        value: getFilterField('promotion'),
        onChange: setField('promotion'),
        items: getAutocompleteItems('promotion'),
        handleItems: () => setAutocompleteItems('promotion'),
        label: 'Promoção',
      },
      sort: {
        value: getSortField('promotion'),
        onChange: setSortField('promotion'),
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('promotion', '', searchFilter));
        setProduct({
          field: `autocompletes.promotion.promotion.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Preço regular',
      value: 'regularPrice',
      width: '0.6fr',
      mask: ({ regularPrice }) => formatBRL(regularPrice),
      filter: {
        mask: formatBRLForList,
        unmask: formatCentsForList,
        value: getFilterField('regularPrice'),
        onChange: setField('regularPrice'),
        items: getAutocompleteItems('regularPrice'),
        handleItems: () => setAutocompleteItems('regularPrice'),
        label: 'Preço Regular',
      },
      sort: {
        value: getSortField('regularPrice'),
        onChange: setSortField('regularPrice'),
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('regularPrice', '', searchFilter));
        setProduct({
          field: `autocompletes.promotion.regularPrice.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Preço promocional',
      value: 'promotionalPrice',
      width: '0.7fr',
      mask: ({ promotionalPrice }) => formatBRL(promotionalPrice),
      filter: {
        mask: formatBRLForList,
        unmask: formatCentsForList,
        value: getFilterField('promotionalPrice'),
        onChange: setField('promotionalPrice'),
        items: getAutocompleteItems('promotionalPrice'),
        handleItems: () => setAutocompleteItems('promotionalPrice'),
        label: 'Preço Promocional',
      },
      sort: {
        value: getSortField('promotionalPrice'),
        onChange: setSortField('promotionalPrice'),
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('promotionalPrice', '', searchFilter));
        setProduct({
          field: `autocompletes.promotion.promotionalPrice.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Início da promoção',
      value: 'promotionalStartDate',
      width: '0.75fr',
      mask: ({ promotionalStartDate }) =>
        new Date(promotionalStartDate).toLocaleString('pt-BR'),
      sort: {
        value: getSortField('promotionalStartDate'),
        onChange: setSortField('promotionalStartDate'),
      },
      interval: [
        {
          value: getFilterField('promotionalStartDate', 'GEQ'),
          onChange: setField('promotionalStartDate', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('promotionalStartDate', 'LT'),
          onChange: setField('promotionalStartDate', 'LT'),
          label: 'Data final',
        },
      ],
      tooltip: {
        translateX: 0.75,
      },
      onClear: () => {
        let filter = setFilterColumn(
          'promotionalStartDate',
          '',
          searchFilter,
          'GEQ',
        );
        filter = setFilterColumn('promotionalStartDate', '', filter, 'LT');
        setSearchFilter(filter);
      },
    },
    {
      text: 'Fim da promoção',
      value: 'promotionalEndDate',
      width: '0.75fr',
      mask: ({ promotionalEndDate }) =>
        new Date(promotionalEndDate).toLocaleString('pt-BR'),
      sort: {
        value: getSortField('promotionalEndDate'),
        onChange: setSortField('promotionalEndDate'),
      },
      interval: [
        {
          value: getFilterField('promotionalEndDate', 'GEQ'),
          onChange: setField('promotionalEndDate', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('promotionalEndDate', 'LT'),
          onChange: setField('promotionalEndDate', 'LT'),
          label: 'Data final',
        },
      ],
      tooltip: {
        translateX: 1,
      },
      onClear: () => {
        let filter = setFilterColumn(
          'promotionalEndDate',
          '',
          searchFilter,
          'GEQ',
        );
        filter = setFilterColumn('promotionalEndDate', '', filter, 'LT');
        setSearchFilter(filter);
      },
    },
    {
      text: loadingProducts ? (
        <S.IconLoaderContainer>
          <SVG.Icon.Loader color="#364856" width={20} height={20} />
        </S.IconLoaderContainer>
      ) : checkedIndexes.length > 0 ? (
        <S.IconControlContainer onClick={addProducts}>
          <SVG.Icon.Plus width={20} height={20} fill="#364856" />
        </S.IconControlContainer>
      ) : (
        <></>
      ),
      value: 'control',
      width: '66px',
      style: {
        cursor: 'pointer',
        margin: 'auto 20px auto auto',
        width: 'calc(100% - 20px)',
      },
    },
  ];

  headers.unshift({
    text: <Form.Input.Checkbox checked={checkAll} onChange={setCheckAll} />,
    value: 'check',
    width: 'auto',
  });

  const currentPage = searchFilter.page ?? 1;
  const itemsPerPage = searchFilter.limit ?? 0;

  return (
    <Page className="page-banner">
      <div
        ref={ref}
        style={{
          position: 'relative',
          transition: 'min-width 500ms, max-width 500ms',
          width: '100%',
          minHeight: 'calc(100vh - 150px)',
          maxHeight: 'calc(100vh - 150px)',
          margin: '0 1rem',
          borderRadius: '5px',
          overflow: 'hidden',
          paddingBottom: 20,
          background: '#DDDDDD',
        }}
      >
        <Table
          onLineClick={onLineClickHandler}
          headers={headers}
          items={items}
          size="small"
          containerStyle={{ height: 'calc(100vh - 210px)' }}
        >
          <Table.Control
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalOfItems={total}
            setPage={setPage}
            setItemsPerPage={setItemsPerPage}
          />
        </Table>
      </div>
    </Page>
  );
}
